import React, { useEffect, useState } from 'react';
import { Calendar, Search } from 'lucide-react';
import { t } from '../../../utils/i18n';
import {
  GA_PRESET_7D,
  GA_PRESET_30D,
  GA_PRESET_CUSTOM,
  GA_RANGE_7D,
  GA_RANGE_30D,
  getGaDateRange,
  todayDateString,
  validateGaDateRange,
} from '../../../utils/gaDashboard';

function DateInput({ value, onChange, min, max, label }) {
  return (
    <label className="flex flex-col gap-1 min-w-[9.5rem]">
      <span className="text-xs text-dark-text-muted">{label}</span>
      <div className="flex items-center gap-2 bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-sm text-dark-text">
        <Calendar className="w-4 h-4 text-dark-text-secondary flex-shrink-0" />
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          className="flex-1 bg-transparent outline-none text-dark-text datetime-local-input min-w-0"
        />
      </div>
    </label>
  );
}

export default function GaDateRangeControls({ preset, appliedRange, onApply, maxDays, maxDaysLabel, getPresetRange = getGaDateRange, maxDate = todayDateString(), validateRange = validateGaDateRange }) {
  const [draftFrom, setDraftFrom] = useState(appliedRange.from);
  const [draftTo, setDraftTo] = useState(appliedRange.to);
  const [errorKey, setErrorKey] = useState(null);
  const [maxError, setMaxError] = useState(false);

  useEffect(() => {
    setDraftFrom(appliedRange.from);
    setDraftTo(appliedRange.to);
    setErrorKey(null);
    setMaxError(false);
  }, [appliedRange.from, appliedRange.to]);

  const handlePreset = (days) => {
    const nextPreset = days === GA_RANGE_7D ? GA_PRESET_7D : GA_PRESET_30D;
    const range = getPresetRange(days);
    setDraftFrom(range.from);
    setDraftTo(range.to);
    setErrorKey(null);
    setMaxError(false);
    onApply({ preset: nextPreset, range });
  };

  const handleCustomApply = () => {
    const validation = validateRange(draftFrom, draftTo);
    if (!validation.valid) {
      setErrorKey(validation.errorKey);
      return;
    }
    if (maxDays && (Date.parse(`${draftTo}T00:00:00Z`) - Date.parse(`${draftFrom}T00:00:00Z`)) / 86400000 >= maxDays) {
      setMaxError(true);
      return;
    }
    setErrorKey(null);
    setMaxError(false);
    onApply({
      preset: GA_PRESET_CUSTOM,
      range: { from: draftFrom, to: draftTo },
    });
  };

  return (
    <div className="flex flex-col gap-3 w-full lg:w-auto">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex rounded-lg border border-dark-border overflow-hidden w-fit">
          {[GA_RANGE_7D, GA_RANGE_30D].map((days) => {
            const activePreset = days === GA_RANGE_7D ? GA_PRESET_7D : GA_PRESET_30D;
            const isActive = preset === activePreset;
            return (
              <button
                key={days}
                type="button"
                onClick={() => handlePreset(days)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-dark-bg'
                    : 'bg-dark-card text-dark-text-secondary hover:text-dark-text'
                }`}
              >
                {days === GA_RANGE_7D
                  ? t('dashboard.ga.dateRange7d')
                  : t('dashboard.ga.dateRange30d')}
              </button>
            );
          })}
        </div>

        <DateInput
          label={t('dashboard.ga.dateRange.from')}
          value={draftFrom}
          onChange={setDraftFrom}
          max={maxDate}
        />
        <DateInput
          label={t('dashboard.ga.dateRange.to')}
          value={draftTo}
          onChange={setDraftTo}
          min={draftFrom || undefined}
          max={maxDate}
        />

        <button
          type="button"
          onClick={handleCustomApply}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dark-border bg-dark-card text-sm font-medium text-dark-text hover:border-primary hover:text-primary transition-colors h-[42px] self-end"
        >
          <Search className="w-4 h-4" />
          {t('dashboard.ga.dateRange.apply')}
        </button>
      </div>

      {preset === GA_PRESET_CUSTOM && (
        <p className="text-xs text-dark-text-muted">
          {t('dashboard.ga.dateRange.customActive', {
            from: appliedRange.from,
            to: appliedRange.to,
          })}
        </p>
      )}

      {errorKey && (
        <p className="text-xs text-red-400">{t(errorKey)}</p>
      )}
      {maxError && <p className="text-xs text-red-400">{maxDaysLabel}</p>}
    </div>
  );
}
